import { sampleTutors } from "../data/sampleTutors";
import { createWhatsAppUrl, getSafeId, normalizePhone } from "./format";

export const STORAGE_KEYS = {
  seedVersion: "dreameer_seed_version",
  tutors: "dreameer_tutors",
  leads: "dreameer_leads",
  bookings: "dreameer_bookings",
  lastBooking: "dreameer_last_booking",
  adminAuth: "dreameer_admin_authenticated",
  parentToken: "dreameer_parent_token",
};

const CURRENT_SEED = "2026-03-v2";

const hasWindow = () => typeof window !== "undefined";
const isFilledString = (value) => typeof value === "string" && value.trim().length > 0;

const readJson = (key, fallback) => {
  if (!hasWindow()) {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    console.warn(`Unable to read ${key} from localStorage`, error);
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (!hasWindow()) {
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event("dreameer:data-updated"));
};

const toArray = (value, fallback = []) =>
  Array.isArray(value) ? value.filter((item) => isFilledString(item)) : fallback;

const sanitizeImage = (value) => {
  if (!isFilledString(value)) {
    return "";
  }
  const nextValue = value.trim();
  if (nextValue.startsWith("data:image/svg+xml")) {
    return "";
  }
  return nextValue;
};

const normalizeReviews = (reviews, fallback = []) => {
  if (!Array.isArray(reviews) || !reviews.length) {
    return fallback;
  }
  return reviews.map((review, index) => ({
    name: isFilledString(review?.name) ? review.name.trim() : `Review ${index + 1}`,
    rating: Number(review?.rating) || 4.8,
    quote: isFilledString(review?.quote)
      ? review.quote.trim()
      : "Clear explanations and reliable support.",
  }));
};

const findSeedTutor = (candidate = {}) =>
  sampleTutors.find((seedTutor) => seedTutor.id === candidate.id || seedTutor.name === candidate.name);

const normalizeTutor = (candidate = {}, index = 0) => {
  const seedTutor = findSeedTutor(candidate) || sampleTutors[index % sampleTutors.length] || {};

  return {
    ...seedTutor,
    ...candidate,
    id: isFilledString(candidate.id) ? candidate.id : seedTutor.id || getSafeId(),
    name: isFilledString(candidate.name) ? candidate.name.trim() : seedTutor.name || "Tutor",
    headline: isFilledString(candidate.headline)
      ? candidate.headline.trim()
      : seedTutor.headline || "Verified tutor",
    experienceYears: Number(candidate.experienceYears ?? seedTutor.experienceYears ?? 1),
    rating: Number(candidate.rating ?? seedTutor.rating ?? 4.8),
    subjects: toArray(candidate.subjects, seedTutor.subjects || []),
    price: Number(candidate.price ?? seedTutor.price ?? 500),
    shortBio: isFilledString(candidate.shortBio)
      ? candidate.shortBio.trim()
      : seedTutor.shortBio || "Structured academic support.",
    fullBio: isFilledString(candidate.fullBio)
      ? candidate.fullBio.trim()
      : seedTutor.fullBio || seedTutor.shortBio || "Structured academic support.",
    qualifications: isFilledString(candidate.qualifications)
      ? candidate.qualifications.trim()
      : seedTutor.qualifications || "Verified tutor",
    category: isFilledString(candidate.category)
      ? candidate.category.trim()
      : seedTutor.category || (seedTutor.subjects || ["General"])[0],
    tags: toArray(candidate.tags, seedTutor.tags || []),
    availabilitySlots: toArray(candidate.availabilitySlots, seedTutor.availabilitySlots || []),
    reviews: normalizeReviews(candidate.reviews, seedTutor.reviews || []),
    image: sanitizeImage(candidate.image),
  };
};

const reconcileTutors = () => {
  const storedTutors = readJson(STORAGE_KEYS.tutors, []);
  if (!storedTutors.length) {
    return sampleTutors;
  }

  const normalizedStored = storedTutors.map((tutor, index) => normalizeTutor(tutor, index));
  const existingIds = new Set(normalizedStored.map((tutor) => tutor.id));
  const missingSeedTutors = sampleTutors.filter((tutor) => !existingIds.has(tutor.id));
  return [...normalizedStored, ...missingSeedTutors];
};

export const initializeDemoData = () => {
  if (!hasWindow()) {
    return;
  }

  const currentSeed = window.localStorage.getItem(STORAGE_KEYS.seedVersion);
  if (currentSeed !== CURRENT_SEED) {
    writeJson(STORAGE_KEYS.tutors, reconcileTutors());
    window.localStorage.setItem(STORAGE_KEYS.seedVersion, CURRENT_SEED);
  }

  if (!window.localStorage.getItem(STORAGE_KEYS.leads)) {
    writeJson(STORAGE_KEYS.leads, []);
  }
  if (!window.localStorage.getItem(STORAGE_KEYS.bookings)) {
    writeJson(STORAGE_KEYS.bookings, []);
  }
  if (!window.localStorage.getItem(STORAGE_KEYS.tutors)) {
    writeJson(STORAGE_KEYS.tutors, sampleTutors);
  }
};

export const getTutors = () => readJson(STORAGE_KEYS.tutors, sampleTutors).map(normalizeTutor);
export const getLeads = () => readJson(STORAGE_KEYS.leads, []);
export const getBookings = () => readJson(STORAGE_KEYS.bookings, []);
export const getLastBooking = () => readJson(STORAGE_KEYS.lastBooking, null);

export const saveTutors = (tutors) => writeJson(STORAGE_KEYS.tutors, tutors.map(normalizeTutor));

export const resetTutorsToSeed = () => {
  if (!hasWindow()) {
    return;
  }
  window.localStorage.setItem(STORAGE_KEYS.seedVersion, CURRENT_SEED);
  writeJson(STORAGE_KEYS.tutors, sampleTutors);
};

export const upsertTutor = (tutor) => {
  const tutors = getTutors();
  const nextTutor = normalizeTutor(
    {
      ...tutor,
      id: tutor.id || getSafeId(),
    },
    tutors.length,
  );
  const index = tutors.findIndex((item) => item.id === nextTutor.id);
  if (index >= 0) {
    tutors[index] = nextTutor;
  } else {
    tutors.unshift(nextTutor);
  }
  saveTutors(tutors);
  return nextTutor;
};

export const deleteTutor = (tutorId) => {
  const nextTutors = getTutors().filter((tutor) => tutor.id !== tutorId);
  saveTutors(nextTutors);
};

export const createBookingAndLead = ({
  tutorId,
  tutorName,
  studentName,
  mobile,
  targetExam,
  preferredSlot,
  source = "web",
}) => {
  const createdAt = new Date().toISOString();
  const cleanMobile = normalizePhone(mobile);
  const booking = {
    id: getSafeId(),
    tutorId,
    tutorName,
    studentName,
    mobile: cleanMobile,
    targetExam,
    preferredSlot,
    source,
    createdAt,
    status: "pending-demo",
  };

  const lead = {
    id: getSafeId(),
    type: "demo-request",
    tutorId,
    tutorName,
    studentName,
    mobile: cleanMobile,
    targetExam,
    preferredSlot,
    source,
    createdAt,
  };

  const bookings = [booking, ...getBookings()];
  const leads = [lead, ...getLeads()];
  writeJson(STORAGE_KEYS.bookings, bookings);
  writeJson(STORAGE_KEYS.leads, leads);
  writeJson(STORAGE_KEYS.lastBooking, booking);

  return {
    booking,
    lead,
    whatsappUrl: createWhatsAppUrl({
      tutorName,
      studentName,
      mobile: cleanMobile,
      slot: preferredSlot,
    }),
  };
};

export const createPopupLead = ({ studentName, mobile, targetExam, source = "popup" }) => {
  const createdAt = new Date().toISOString();
  const cleanMobile = normalizePhone(mobile);
  const lead = {
    id: getSafeId(),
    type: "popup-demo",
    tutorId: "general",
    tutorName: "General Counseling",
    studentName,
    mobile: cleanMobile,
    targetExam,
    preferredSlot: "Callback",
    source,
    createdAt,
  };

  const leads = [lead, ...getLeads()];
  writeJson(STORAGE_KEYS.leads, leads);

  return lead;
};

export const isAdminAuthenticated = () => {
  if (!hasWindow()) {
    return false;
  }
  return window.sessionStorage.getItem(STORAGE_KEYS.adminAuth) === "true";
};

export const setAdminAuthenticated = (value) => {
  if (!hasWindow()) {
    return;
  }
  if (value) {
    window.sessionStorage.setItem(STORAGE_KEYS.adminAuth, "true");
  } else {
    window.sessionStorage.removeItem(STORAGE_KEYS.adminAuth);
  }
};

export const getParentToken = () => {
  if (!hasWindow()) {
    return "";
  }
  return window.localStorage.getItem(STORAGE_KEYS.parentToken) || "";
};

export const setParentToken = (token) => {
  if (!hasWindow()) {
    return;
  }
  window.localStorage.setItem(STORAGE_KEYS.parentToken, token);
};

export const clearParentToken = () => {
  if (!hasWindow()) {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEYS.parentToken);
};
